import React, { useState } from "react";
import { Heart, Sword, Shield, Zap, Loader2, ImagePlus, ArrowUpCircle, Sparkles, X, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useGame } from "@/contexts/GameContext";
import { toast } from "sonner";
import axios from "axios";
import soundManager from "@/utils/SoundManager";
import { t, tf } from "@/utils/i18n";
import { resolveImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_META = {
  common:    { cls: "",                     labelKey: "rarity_common",    color: "#94a3b8" },
  uncommon:  { cls: "rarity-uncommon",      labelKey: "rarity_uncommon",  color: "#22c55e" },
  rare:      { cls: "rarity-rare",          labelKey: "rarity_rare",      color: "#3b82f6" },
  epic:      { cls: "rarity-epic",          labelKey: "rarity_epic",      color: "#a855f7" },
  legendary: { cls: "rarity-legendary",     labelKey: "rarity_legendary", color: "#f59e0b" },
};

function cardTypeClass(type) {
  if (type === "ally") return "card-ally";
  if (type === "ability") return "card-ability";
  return "card-object";
}

export default function GameCard({ card, type, onEquip, onUse, inBattle, variant = "full" }) {
  const [generatingArt, setGeneratingArt] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [using, setUsing] = useState(false);
  const [dusting, setDusting] = useState(false);
  const [dustConfirm, setDustConfirm] = useState(false);
  const [imageUrl, setImageUrl] = useState(card?.image_url);
  const [expanded, setExpanded] = useState(false);
  const { token, API } = useAuth();
  const { game, upgradeCard, useItem: consumeItem, loadGame } = useGame();

  if (!card) return null;

  const lang = game?.language || "en";
  const typeClass = cardTypeClass(type);
  const borderColor = type === "ally" ? "#064e3b" : type === "ability" ? "#581c87" : "#d4af37";
  const cardLevel = card.level || 1;
  const isMaxLevel = cardLevel >= 5;
  const upgradeCost = cardLevel * 30;
  const playerXp = game?.character?.xp || 0;
  const rarity = (card.rarity || "common").toLowerCase();
  const rarityMeta = RARITY_META[rarity] || RARITY_META.common;
  const isFoil = !!card.foil;

  const generateArtwork = async (regenerate = false) => {
    if (!game?.id || generatingArt) return;
    setGeneratingArt(true);
    soundManager.playClick();
    try {
      const category = type === "ally" ? "allies" : type === "ability" ? "abilities" : "objects";
      const res = await axios.post(`${API}/game/${game.id}/card/generate-artwork`, {
        card_id: card.id, card_category: category, regenerate,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 });
      setImageUrl(res.data.image_url);
      // Refresh game state so the new image_url persists across tab changes / remounts
      try { await loadGame(game.id); } catch { /* non-critical */ }
      soundManager.playNewItem();
      toast.success(`Artwork generated for ${card.name}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate artwork");
    } finally {
      setGeneratingArt(false);
    }
  };

  const handleUpgrade = async () => {
    if (!game?.id || upgrading || isMaxLevel) return;
    if (playerXp < upgradeCost) { toast.error(t(lang, 'not_enough_xp')); return; }
    setUpgrading(true);
    soundManager.playClick();
    try {
      const category = type === "ally" ? "allies" : type === "ability" ? "abilities" : "objects";
      await upgradeCard(game.id, card.id, category);
      soundManager.playNewItem();
      toast.success(t(lang, 'card_upgraded'));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  const hasUsableEffect = card.effect && card.effect !== "none" && card.effect_value > 0;
  const isBattleOnly = card.effect === "damage" || card.effect === "defense";
  const canUseNow = hasUsableEffect && (!isBattleOnly || inBattle);

  const handleDust = async () => {
    if (!game?.id || dusting || card.is_basic) return;
    if (!dustConfirm) {
      // First click → arm confirmation, auto-cancel after 4s
      setDustConfirm(true);
      setTimeout(() => setDustConfirm(false), 4000);
      return;
    }
    setDustConfirm(false);
    setDusting(true);
    soundManager.playClick();
    try {
      const category = type === "ally" ? "allies" : type === "ability" ? "abilities" : "objects";
      const res = await axios.post(`${API}/game/${game.id}/card/dust`, {
        card_id: card.id, card_category: category,
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
      toast.success(lang.startsWith("it") ? `+${res.data.dust_gained} polvere` : `+${res.data.dust_gained} dust`);
      await loadGame(game.id);
      setExpanded(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dust failed");
    } finally {
      setDusting(false);
    }
  };

  const handleUse = async () => {
    if (!game?.id || using || !canUseNow) return;
    setUsing(true);
    soundManager.playClick();
    try {
      const category = type === "ally" ? "allies" : type === "ability" ? "abilities" : "objects";
      const result = await consumeItem(game.id, card.id, category);
      soundManager.playNewItem();
      if (result.effect === "heal") toast.success(tf(lang, 'item_heal_msg', [result.effect_value]));
      else if (result.effect === "damage") toast.success(tf(lang, 'item_damage_msg', [result.effect_value]));
      else if (result.effect === "defense") toast.success(tf(lang, 'item_defense_msg', [result.effect_value]));
      else toast.success(t(lang, 'item_used'));
      setExpanded(false);
      if (onUse) onUse(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Use failed");
    } finally {
      setUsing(false);
    }
  };

  const rarityClass = rarityMeta.cls;
  const foilClass = isFoil ? "card-foil" : "";

  // ========== COMPACT VARIANT (inventory grid) ==========
  if (variant === "compact") {
    let pressTimer = null;
    let longPressed = false;
    const LONG_PRESS_MS = 450;
    const startPress = (e) => {
      longPressed = false;
      pressTimer = setTimeout(() => {
        longPressed = true;
        const el = e?.currentTarget;
        if (el) el.classList.add("card-preview-scale");
      }, LONG_PRESS_MS);
    };
    const endPress = (e) => {
      if (pressTimer) clearTimeout(pressTimer);
      const el = e?.currentTarget;
      if (el) el.classList.remove("card-preview-scale");
      // If it was a long press, don't trigger click-to-expand
      if (longPressed) {
        longPressed = false;
        return;
      }
    };
    return (
      <>
        <div
          className={`mtg-card ${typeClass} ${rarityClass} variant-compact ${card.is_new ? 'card-new' : ''}`}
          data-testid={`card-${card.id}`}
          onClick={(e) => { if (!longPressed) setExpanded(true); }}
          onPointerDown={startPress}
          onPointerUp={endPress}
          onPointerLeave={endPress}
          onPointerCancel={endPress}
          onContextMenu={(e) => e.preventDefault()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(true); } }}
        >
          <div className={`mtg-inner-frame ${isFoil ? "card-foil" : ""}`}>
            {/* Header bar: Title + Mana */}
            <div className="mtg-header-bar">
              <span className="mtg-card-title">{card.name}</span>
              <div className="mtg-header-right">
                {cardLevel > 1 && (
                  <span className="mtg-level-badge">Lv{cardLevel}</span>
                )}
                {card.mana_cost > 0 && (
                  <div className="mtg-mana-gem">
                    <span>{card.mana_cost}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Artwork */}
            <div className="mtg-art-frame">
              {imageUrl ? (
                <img src={resolveImageUrl(imageUrl, BACKEND_URL)} alt={card.name} className="mtg-art-image" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <div className="mtg-no-art">
                  <ImagePlus size={16} />
                </div>
              )}
            </div>

            {/* Stat plaque at bottom right of compact artwork */}
            {(type === "ally" || (type === "object" && (card.attack > 0 || card.defense > 0))) && (
              <div className="mtg-stat-plaque">
                {type === "ally" ? (
                  <>
                    <span>{card.attack}</span>/<span>{card.hp}</span>
                  </>
                ) : (
                  <>
                    {card.attack > 0 && <span>+{card.attack}</span>}
                    {card.attack > 0 && card.defense > 0 && <span>/</span>}
                    {card.defense > 0 && <span>+{card.defense}</span>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Full-view modal on click */}
        {expanded && createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-3 overflow-y-auto overscroll-contain"
            onClick={() => setExpanded(false)}
            data-testid={`card-modal-${card.id}`}
          >
            <div className="relative max-w-sm w-full my-auto max-h-[calc(100dvh-1.5rem)] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="relative">
                <button
                  onClick={() => setExpanded(false)}
                  className="absolute top-2 right-2 z-20 bg-black/80 hover:bg-[#8b0000]/80 border border-[#cfb53b]/60 rounded-full w-9 h-9 flex items-center justify-center text-[#cfb53b] shadow-lg transition-colors"
                  aria-label="Close"
                  data-testid={`card-modal-close-${card.id}`}
                >
                  <X size={18} />
                </button>
                <GameCardFullView
                  card={card}
                  type={type}
                  imageUrl={imageUrl}
                  generatingArt={generatingArt}
                  onGenerateArt={generateArtwork}
                  upgrading={upgrading}
                  onUpgrade={handleUpgrade}
                  using={using}
                  onUse={handleUse}
                  canUseNow={canUseNow}
                  hasUsableEffect={hasUsableEffect}
                  isBattleOnly={isBattleOnly}
                  inBattle={inBattle}
                  onEquip={onEquip}
                  cardLevel={cardLevel}
                  isMaxLevel={isMaxLevel}
                  upgradeCost={upgradeCost}
                  playerXp={playerXp}
                  rarity={rarity}
                  rarityMeta={rarityMeta}
                  isFoil={isFoil}
                  typeClass={typeClass}
                  borderColor={borderColor}
                  lang={lang}
                  onDust={handleDust}
                  dusting={dusting}
                  dustConfirm={dustConfirm}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // ========== FULL VARIANT (default) ==========
  return (
    <GameCardFullView
      card={card}
      type={type}
      imageUrl={imageUrl}
      generatingArt={generatingArt}
      onGenerateArt={generateArtwork}
      upgrading={upgrading}
      onUpgrade={handleUpgrade}
      using={using}
      onUse={handleUse}
      canUseNow={canUseNow}
      hasUsableEffect={hasUsableEffect}
      isBattleOnly={isBattleOnly}
      inBattle={inBattle}
      onEquip={onEquip}
      cardLevel={cardLevel}
      isMaxLevel={isMaxLevel}
      upgradeCost={upgradeCost}
      playerXp={playerXp}
      rarity={rarity}
      rarityMeta={rarityMeta}
      isFoil={isFoil}
      typeClass={typeClass}
      borderColor={borderColor}
      lang={lang}
      onDust={handleDust}
      dusting={dusting}
      dustConfirm={dustConfirm}
    />
  );
}

function GameCardFullView({
  card, type, imageUrl, generatingArt, onGenerateArt, upgrading, onUpgrade,
  using, onUse, canUseNow, hasUsableEffect, isBattleOnly, inBattle, onEquip,
  cardLevel, isMaxLevel, upgradeCost, playerXp, rarity, rarityMeta, isFoil,
  typeClass, borderColor, lang, onDust, dusting, dustConfirm,
}) {
  const rarityClass = rarityMeta.cls;
  const hasPlaque = type === "ally" || (type === "object" && (card.attack > 0 || card.defense > 0));

  return (
    <div className="mtg-card-wrapper animate-fadeInUp">
      {/* Visual MTG Card */}
      <div 
        className={`mtg-card ${typeClass} ${rarityClass} ${card.is_new ? 'card-new' : ''}`}
        data-testid={`card-${card.id}`}
      >
        <div className={`mtg-inner-frame ${isFoil ? "card-foil" : ""}`}>
          {/* Header Bar */}
          <div className="mtg-header-bar">
            <span className="mtg-card-title">{card.name}</span>
            <div className="mtg-header-right">
              {card.mana_cost > 0 && (
                <div className="mtg-mana-gem" title={`Mana cost: ${card.mana_cost}`}>
                  <span>{card.mana_cost}</span>
                </div>
              )}
            </div>
          </div>

          {/* Art Frame */}
          <div className="mtg-art-frame relative group">
            {imageUrl ? (
              <>
                <img src={resolveImageUrl(imageUrl, BACKEND_URL)} alt={card.name} className="mtg-art-image" onError={(e) => { e.target.style.display = 'none'; }} />
                {type === "ally" && card.ally_origin && (
                  <button
                    data-testid={`regen-ally-art-${card.id}`}
                    onClick={() => onGenerateArt(true)}
                    disabled={generatingArt}
                    title={lang.startsWith("it")
                      ? "Rigenera in versione compagno (mossa amichevole, sigillo del legame)"
                      : "Regenerate as companion (friendly stance, bond sigil)"}
                    className="absolute bottom-1 right-1 bg-black/75 hover:bg-[#34d399]/90 hover:text-black text-[#34d399] border border-[#34d399]/40 rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1 opacity-90 transition-colors z-20"
                  >
                    {generatingArt ? <Loader2 size={10} className="animate-spin" /> : <ImagePlus size={10} />}
                    {lang.startsWith("it") ? "Rigenera" : "Regen"}
                  </button>
                )}
              </>
            ) : (
              <button
                data-testid={`gen-art-${card.id}`}
                onClick={() => onGenerateArt(false)}
                disabled={generatingArt}
                className="w-full h-full flex flex-col items-center justify-center hover:bg-white/[0.03] transition-colors"
              >
                {generatingArt ? <Loader2 size={16} className="animate-spin text-[#cfb53b]" /> : (
                  <div className="text-center p-2">
                    <ImagePlus size={18} className="mx-auto text-[#64748b] mb-1" />
                    <span className="text-[10px] text-[#64748b] font-heading">{t(lang, 'generate_art')}</span>
                  </div>
                )}
              </button>
            )}
          </div>

          {/* Type Line */}
          <div className="mtg-type-line">
            <span className="mtg-type-text">
              {t(lang, `type_${type}`)} {card.boss_origin && ` - ${card.boss_origin}`}
            </span>
            <div className="mtg-type-right">
              {cardLevel > 1 && (
                <span className="mtg-level-badge">{t(lang, 'level')}{cardLevel}</span>
              )}
              {rarity !== "common" && (
                <span className="mtg-rarity-indicator font-heading" style={{ color: rarityMeta.color }} title={t(lang, rarityMeta.labelKey)}>
                  ✦
                </span>
              )}
            </div>
          </div>

          {/* Rules / Text Box */}
          <div className="mtg-rules-box">
            <div className="mtg-description">
              {type === "ability" && (
                <div className="mb-1 font-heading text-[9px] uppercase tracking-wider">
                  {card.effect === "damage" && <span className="text-red-600">DANNO: {card.effect_value}</span>}
                  {card.effect === "heal" && <span className="text-green-600">CURA: {card.effect_value}</span>}
                  {card.effect === "buff" && <span className="text-amber-600">POTENZIAMENTO: +{card.effect_value}</span>}
                  {card.effect === "debuff" && <span className="text-purple-600">DEBUFF: -{card.effect_value}</span>}
                </div>
              )}
              <p className="leading-snug">{card.description}</p>
              {card.boss_origin && (
                <p className="mtg-flavor-text">
                  {lang.startsWith("it") ? `Leggenda di: ${card.boss_origin}` : `Legend of: ${card.boss_origin}`}
                </p>
              )}
            </div>

            {/* Stat Plaque for combat stats */}
            {hasPlaque && (
              <div className="mtg-stat-plaque">
                {type === "ally" ? (
                  <>
                    <span>{card.attack}</span> / <span className="text-emerald-700">{card.hp}</span>
                  </>
                ) : (
                  <>
                    {card.attack > 0 && <span>+{card.attack}⚔</span>}
                    {card.attack > 0 && card.defense > 0 && <span>/</span>}
                    {card.defense > 0 && <span className="text-blue-700">+{card.defense}🛡</span>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons underneath the MTG card */}
      <div className="bg-[#111115]/95 border border-white/10 rounded-lg p-2.5 flex flex-col gap-2">
        {/* Rarity & Foil Info banner in Actions Panel */}
        {(rarity !== "common" || isFoil) && (
          <div className="flex flex-wrap items-center gap-1.5 justify-center py-1 border-b border-white/5">
            {rarity !== "common" && (
              <span className="text-[9px] font-heading uppercase px-2 py-0.5 rounded bg-black/40 border border-white/5" style={{ color: rarityMeta.color }}>
                {t(lang, rarityMeta.labelKey)}
              </span>
            )}
            {isFoil && (
              <span className="text-[9px] font-heading uppercase px-2 py-0.5 rounded bg-black/40 text-[#fef08a] border border-[#fef08a]/20 flex items-center gap-0.5">
                <Sparkles size={9} /> FOIL
              </span>
            )}
          </div>
        )}

        {/* Upgrade Card */}
        {!card.is_basic && (
          <div>
            {isMaxLevel ? (
              <div className="text-center py-1 bg-black/25 border border-white/5 rounded">
                <span className="text-[10px] text-[#cfb53b] font-heading">{t(lang, 'max_level')}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <button
                  data-testid={`upgrade-${card.id}`}
                  onClick={onUpgrade}
                  disabled={upgrading || playerXp < upgradeCost}
                  className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-heading transition-all ${
                    playerXp >= upgradeCost 
                      ? 'bg-[#cfb53b]/10 text-[#cfb53b] border border-[#cfb53b]/30 hover:bg-[#cfb53b]/20' 
                      : 'bg-[#2d3748]/30 text-[#64748b] border border-white/5 cursor-not-allowed'
                  }`}
                >
                  {upgrading ? <Loader2 size={10} className="animate-spin" /> : <ArrowUpCircle size={10} />}
                  {t(lang, 'upgrade')} - {tf(lang, 'upgrade_cost', [upgradeCost])}
                </button>
                {/* Upgrade warning */}
                {(type === "ability" || card.type === "ability") && (cardLevel === 2 || cardLevel === 4) && (
                  <div
                    data-testid={`upgrade-warning-${card.id}`}
                    className="text-[9px] text-amber-400/90 leading-tight text-center px-1 py-0.5"
                  >
                    ⚡ {lang.startsWith("it")
                      ? `Costo mana +1 a Lv${cardLevel + 1}`
                      : `Mana cost +1 at Lv${cardLevel + 1}`}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Equip Object */}
        {type === "object" && card.equippable && onEquip && (
          <div className="flex gap-1">
            {card.slot && card.slot !== "none" ? (
              <Button data-testid={`equip-btn-${card.id}`} variant="ghost" size="sm"
                onClick={() => { soundManager.playClick(); onEquip(card.id, card.slot); }}
                className="w-full text-[10px] text-[#cfb53b] hover:bg-[#cfb53b]/10 h-7 border border-[#cfb53b]/20">
                {t(lang, 'equip_as')} {card.slot}
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => { soundManager.playClick(); onEquip(card.id, "weapon"); }} className="flex-1 text-[10px] text-[#cfb53b] hover:bg-[#cfb53b]/10 h-7 border border-[#cfb53b]/20">{t(lang, 'weapon')}</Button>
                <Button variant="ghost" size="sm" onClick={() => { soundManager.playClick(); onEquip(card.id, "armor"); }} className="flex-1 text-[10px] text-[#cfb53b] hover:bg-[#cfb53b]/10 h-7 border border-[#cfb53b]/20">{t(lang, 'armor_slot')}</Button>
                <Button variant="ghost" size="sm" onClick={() => { soundManager.playClick(); onEquip(card.id, "accessory"); }} className="flex-1 text-[10px] text-[#cfb53b] hover:bg-[#cfb53b]/10 h-7 border border-[#cfb53b]/20">Acc.</Button>
              </>
            )}
          </div>
        )}

        {/* Use Item */}
        {hasUsableEffect && !card.is_basic && (
          <div className={!canUseNow ? 'opacity-50' : ''}>
            <Button data-testid={`use-btn-${card.id}`} variant="ghost" size="sm" onClick={onUse} disabled={!canUseNow || using}
              className={`w-full text-[10px] font-heading h-7 border ${
                canUseNow 
                  ? 'text-green-400 border-green-500/20 hover:bg-green-400/10' 
                  : 'text-[#64748b] border-white/5 cursor-not-allowed'
              }`}>
              {using ? <Loader2 size={10} className="animate-spin" /> : (
                <>
                  <Zap size={10} className="mr-1" />
                  {t(lang, 'use_item')}
                  {isBattleOnly && !inBattle && (<span className="ml-1 text-[8px] normal-case">({t(lang, 'item_battle_only')})</span>)}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Dust Card */}
        {!card.is_basic && onDust && (
          <div>
            <Button
              data-testid={`dust-btn-${card.id}`}
              variant="ghost"
              size="sm"
              onClick={onDust}
              disabled={dusting}
              className={`w-full text-[10px] font-heading h-7 transition-colors border ${
                dustConfirm
                  ? "bg-red-500/20 text-red-300 hover:bg-red-500/30 border-red-500/40 animate-pulse"
                  : "text-purple-400 border-purple-500/20 hover:bg-purple-400/10"
              }`}
            >
              {dusting ? <Loader2 size={10} className="animate-spin" /> : dustConfirm ? (
                <>
                  <Recycle size={10} className="mr-1" />
                  {lang.startsWith("it") ? "Sei sicuro? Clicca di nuovo" : "Sure? Click again to confirm"}
                </>
              ) : (
                <>
                  <Recycle size={10} className="mr-1" />
                  {lang.startsWith("it") ? "Distruggi per polvere" : "Dust (destroy)"}
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
